import React, { RefCallback } from "react";
import { Typography } from "@mui/material";
import { MuiTelInput } from "mui-tel-input";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CrudFieldController from "@/components/tables/fields/CrudFieldController";
import { requiredRuleWithFallback } from "@/components/tables/fields/fieldRules";
import { textFieldSx } from "@/components/tables/fields/fieldStyles";
import type { CrudModalField } from "@/components/tables/fields/crudModalField";
import styles from "@/components/tables/fields/crudField.module.css";

export interface CrudPhoneFieldProps {
  field: Pick<CrudModalField, "id" | "header" | "required" | "errorMessage">;
  validationTargetRef?: RefCallback<HTMLElement>;
  onChange?: (value: string) => void;
}

const CrudPhoneField = ({
  field,
  validationTargetRef,
  onChange,
}: CrudPhoneFieldProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  return (
    <div ref={validationTargetRef}>
      <Typography variant="body1" className={styles.phoneInputLabel}>
        {field.header}
        {field.required && (
          <span className={styles.labelRequired}>{"\u{002A}"}</span>
        )}
      </Typography>
      <CrudFieldController
        name={field.id as string}
        control={control}
        rules={requiredRuleWithFallback(field, t)}
        render={({ field: { onChange: setFormValue, value, ...rest } }) => (
          <MuiTelInput
            {...rest}
            placeholder={t("input_your_phone_number")}
            defaultCountry="DE"
            langOfCountryName="DE"
            value={value || ""}
            onChange={(newValue: string) => {
              setFormValue(newValue);
              onChange?.(newValue);
            }}
            className={styles.fullWidth}
            sx={textFieldSx}
          />
        )}
      />
    </div>
  );
};

export default CrudPhoneField;
